import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';

const W = 1080;
const H = 1920;

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');

const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export const TrackerRecapGraphic = ({ recap, rangeLabel, agentName, agentPicture }) => {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !recap) return;
    const ctx = canvas.getContext('2d');
    const accent = '#23ACD5';
    const accent2 = '#00E5FF';

    try { await document.fonts.ready; } catch { /* noop */ }
    const [logo, avatar] = await Promise.all([loadImage('/breeze-logo-trim.png'), loadImage(agentPicture)]);

    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, '#0B1437');
    bg.addColorStop(0.5, '#111C44');
    bg.addColorStop(1, '#0A1030');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    const glow = ctx.createRadialGradient(900, 260, 0, 900, 260, 700);
    glow.addColorStop(0, `${accent}55`);
    glow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
    const glow2 = ctx.createRadialGradient(120, 1700, 0, 120, 1700, 620);
    glow2.addColorStop(0, 'rgba(67,24,255,0.45)');
    glow2.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow2;
    ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 2;
    for (let i = 1; i < 9; i += 1) { ctx.beginPath(); ctx.moveTo((W / 9) * i, 0); ctx.lineTo((W / 9) * i, H); ctx.stroke(); }
    for (let i = 1; i < 16; i += 1) { ctx.beginPath(); ctx.moveTo(0, (H / 16) * i); ctx.lineTo(W, (H / 16) * i); ctx.stroke(); }

    ctx.strokeStyle = `${accent}66`;
    ctx.lineWidth = 6;
    roundRect(ctx, 30, 30, W - 60, H - 60, 48);
    ctx.stroke();

    // Logo
    if (logo) {
      const lw = 300;
      ctx.drawImage(logo, (W - lw) / 2, 92, lw, (logo.height / logo.width) * lw);
    } else {
      ctx.fillStyle = '#FFFFFF';
      ctx.font = "800 54px Manrope, sans-serif";
      ctx.textAlign = 'center';
      ctx.fillText('BREEZE', W / 2, 150);
    }

    // Eyebrow + headline
    ctx.textAlign = 'center';
    ctx.fillStyle = accent;
    ctx.font = "800 30px Manrope, sans-serif";
    ctx.letterSpacing = '8px';
    ctx.fillText('ACTIVITY RECAP', W / 2, 290);
    ctx.letterSpacing = '0px';

    ctx.fillStyle = '#FFFFFF';
    ctx.font = "800 84px Manrope, sans-serif";
    ctx.fillText('Work the numbers.', W / 2, 390);
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.font = "600 34px Manrope, sans-serif";
    ctx.fillText(rangeLabel, W / 2, 448);

    // Agent row
    const ay = 560;
    ctx.save();
    ctx.beginPath();
    ctx.arc(W / 2 - 150, ay, 54, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    if (avatar) {
      const side = Math.min(avatar.width, avatar.height);
      ctx.drawImage(avatar, (avatar.width - side) / 2, (avatar.height - side) / 2, side, side, W / 2 - 204, ay - 54, 108, 108);
    } else {
      ctx.fillStyle = accent;
      ctx.fillRect(W / 2 - 204, ay - 54, 108, 108);
      ctx.fillStyle = '#0B1437';
      ctx.font = "800 42px Manrope, sans-serif";
      ctx.textBaseline = 'middle';
      ctx.fillText(initials(agentName), W / 2 - 150, ay);
      ctx.textBaseline = 'alphabetic';
    }
    ctx.restore();
    ctx.strokeStyle = accent;
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.arc(W / 2 - 150, ay, 57, 0, Math.PI * 2);
    ctx.stroke();

    ctx.textAlign = 'left';
    ctx.fillStyle = '#FFFFFF';
    ctx.font = "800 44px Manrope, sans-serif";
    ctx.fillText(agentName || 'Breeze Agent', W / 2 - 70, ay - 4);
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = "600 28px Manrope, sans-serif";
    ctx.fillText('Breeze Financial Group', W / 2 - 70, ay + 38);

    // Stat tiles — 2 columns x 3 rows
    const tiles = [
      { label: 'DIALS', value: recap.dials_made },
      { label: 'CONTACTS', value: recap.contacts_made },
      { label: 'APPTS BOOKED', value: recap.appointments_set },
      { label: 'APPTS SHOWED', value: recap.appointments_showed },
      { label: 'PRESENTATIONS', value: recap.presentations_given },
      { label: 'POLICIES SOLD', value: recap.sales_made },
    ];
    const gx = 70;
    const gy = 690;
    const gap = 26;
    const tw = (W - gx * 2 - gap) / 2;
    const th = 236;
    tiles.forEach((t, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = gx + col * (tw + gap);
      const y = gy + row * (th + gap);
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      roundRect(ctx, x, y, tw, th, 32);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.10)';
      ctx.lineWidth = 3;
      roundRect(ctx, x, y, tw, th, 32);
      ctx.stroke();

      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = "700 24px Manrope, sans-serif";
      ctx.letterSpacing = '4px';
      ctx.fillText(t.label, x + tw / 2, y + 62);
      ctx.letterSpacing = '0px';

      ctx.fillStyle = '#FFFFFF';
      ctx.font = "800 104px Manrope, sans-serif";
      ctx.fillText(String(t.value ?? 0), x + tw / 2, y + 176);
    });

    // AP panel
    const py = gy + 3 * (th + gap) + 10;
    const ph = 250;
    ctx.fillStyle = `${accent}1F`;
    roundRect(ctx, gx, py, W - gx * 2, ph, 36);
    ctx.fill();
    ctx.strokeStyle = `${accent}99`;
    ctx.lineWidth = 4;
    roundRect(ctx, gx, py, W - gx * 2, ph, 36);
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = "700 26px Manrope, sans-serif";
    ctx.letterSpacing = '5px';
    ctx.fillText('TOTAL AP SUBMITTED', W / 2, py + 66);
    ctx.letterSpacing = '0px';

    const amount = `$${Math.round(recap.ap_submitted || 0).toLocaleString()}`;
    let size = 132;
    ctx.font = `800 ${size}px Manrope, sans-serif`;
    while (ctx.measureText(amount).width > W - gx * 2 - 80 && size > 60) {
      size -= 6;
      ctx.font = `800 ${size}px Manrope, sans-serif`;
    }
    ctx.save();
    ctx.shadowColor = accent2;
    ctx.shadowBlur = 44;
    ctx.fillStyle = accent2;
    ctx.fillText(amount, W / 2, py + 196);
    ctx.restore();

    // Footer
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.font = "600 26px Manrope, sans-serif";
    ctx.fillText(`${fmtDate(recap.range_start)}${recap.range_start !== recap.range_end ? ` – ${fmtDate(recap.range_end)}` : ''}`, W / 2, H - 92);

    setReady(true);
  }, [recap, rangeLabel, agentName, agentPicture]);

  useEffect(() => { setReady(false); draw(); }, [draw]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `breeze-activity-${recap.range_start}_${recap.range_end}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  return (
    <div className="space-y-4" data-testid="tracker-recap-graphic">
      <div className="relative mx-auto max-w-[320px] overflow-hidden rounded-2xl border border-[#E9EDF7] bg-[#0B1437]">
        <canvas ref={canvasRef} width={W} height={H} className="block w-full" data-testid="tracker-recap-canvas" />
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0B1437]">
            <Loader2 className="h-8 w-8 animate-spin text-[#23ACD5]" />
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={download}
        disabled={!ready}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#23ACD5] to-[#00E5FF] px-6 py-3.5 font-['Manrope'] text-sm font-extrabold text-[#06263A] shadow-[0_8px_28px_rgba(35,172,213,0.45)] transition-transform hover:scale-[1.01] disabled:opacity-60"
        data-testid="tracker-recap-download-btn"
      >
        <Download className="h-4 w-4" />
        Save graphic for social
      </button>
    </div>
  );
};
